import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { StringUtilsHelper } from 'src/app/utils/string-utils-helper';
import { BiStgPlayerBiVizDimsDatasource } from '../../common/sql-maps/bicore-player-dims-sql-map';
import { BiVizDashboardCampaignsDatasource } from '../../mopa-campaigns/sql-maps/biviz-dashboard-campaigns-sql-map';
import { BIVizDashboardSales } from '../../mopa-sales/sql-maps/biviz-dashboard-sales-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
export interface CampaignsMetricsByDayNoDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class CampaignsMetricsByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    CampaignsMetricsByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'CampaignsMetricsByDayNoDataLoaderFactory';

    getInstance(options: CampaignsMetricsByDayNoDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                const campaignMastertableDatasource =
                    BiVizDashboardCampaignsDatasource;

                const campaignsMastertableSqlMap =
                    campaignMastertableDatasource.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    campaignsMastertableSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                const activeAll =
                    queryContext.settings[
                        QueryContextSettingID.CAMPAIGN_ACTIVE_ALL
                    ] ?? 'all';
                const activeCampaignSQL: Partial<ICompiledSQLTokens> =
                    activeAll === 'all'
                        ? {}
                        : {
                              where: [`active_campaign IS TRUE`],
                          };

                const campaignsMastertableSql =
                    campaignMastertableDatasource.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options,
                        mergeSqlTokensArray([activeCampaignSQL])
                    );

                const salesDatasource = BIVizDashboardSales;

                // identify columns not supported in datasource
                const salesDsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    salesDatasource.sqlMap.columns
                );

                const salesDsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    salesDsColumns.missing
                );

                // ignore columns that are not supported in this query
                // const ignoredContextColumnIds = [
                //     'ACTIVITY_DATE',
                //     'ACTIVITY_APP_VERSION',
                //     'ACTIVITY_APP_VERSION_GROUP_MINOR',
                // ];

                //filter out ignored columns from dsColumns.present
                // salesDsColumns.present = salesDsColumns.present.filter(
                //     (col) => !ignoredContextColumnIds.includes(col.id)
                // );

                const salesSQL =
                    salesDatasource.sqlStatementsCompiler.compileSQLStatements(
                        salesDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const usersDatasource = BiVizDashboardUsersActivity;

                const usersDsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    usersDatasource.sqlMap.columns
                );
                const usersDsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    usersDsColumns.missing
                );
                const usersSQL = usersDatasource.compileSQLStatements(
                    usersDsColumns.present,
                    queryContext.settings,
                    options
                );

                const playerBiVizDims = BiStgPlayerBiVizDimsDatasource;
                // identify columns not supported in datasource
                const playerDims = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    playerBiVizDims.sqlMap.columns
                );

                const playerDimsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(playerDims.missing);

                const playerDimsSQL = playerBiVizDims.compileSQLStatements(
                    playerDims.present,
                    queryContext.settings,
                    options,
                    { where: ['player_dim.validation.unified_valid'] }
                );

                const windowRunning = `${StringUtilsHelper.prefixIfNotNull(
                    playerDimsSQL.splitGroup,
                    'PARTITION BY  c.'
                )} ORDER BY c.day_no`;

                const revenueType = (() => {
                    let setting =
                        queryContext.settings[
                            QueryContextSettingID.REVENUE_TYPE
                        ] ?? 'total';
                    if (setting === 'inapp') {
                        return 'inapp_';
                    } else if (setting === 'ad') {
                        return 'ad_';
                    } else if (setting === 'total') {
                        return '';
                    } else {
                        throw new Error(
                            `Unknown revenue type setting: ${setting}`
                        );
                    }
                })();
                // pln/usd
                const currency =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';
                const settingSetGross =
                    queryContext.settings[QueryContextSettingID.NET_GROSS] ??
                    'net';
                const netGross = settingSetGross === 'net' ? 'net_' : 'gross_';

                /**
                 * Generate join condition SQL
                 * @param aliasA
                 * @param aliasB
                 * @returns Example `ON dsA.COLUMN_ID = dsB.COLUMN_ID`
                 */
                const finalDataJoinConditionSql = (options: {
                    aliasA: string;
                    aliasB: string;
                    conditions?: string[];
                }) => {
                    const splitDatasourceColumnIds =
                        queryContext.datasourceColumns
                            .filter((col) => col.state.isSplitApplied)
                            .map(
                                (col) =>
                                    `${options.aliasA}.${col.id} = ${options.aliasB}.${col.id}`
                            );
                    // preadd join condition if there are any conditions
                    if (options.conditions && options.conditions.length > 0) {
                        splitDatasourceColumnIds.unshift(...options.conditions);
                    }
                    // join them with AND
                    return splitDatasourceColumnIds.length > 0
                        ? `ON ${splitDatasourceColumnIds.join(' AND ')}`
                        : '';
                };
                const revenueSql = `${revenueType}revenue_${netGross}${currency}`;

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH revenue AS
(
  SELECT
    ${salesSQL.splitSelect}
    day_no,
    SUM(${revenueSql}) AS ${revenueSql},
    SUM(transactions) AS transactions
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      salesDatasource.sqlMap.dataset,
      salesDatasource.sqlMap.tableId
  )}\` ds
  ${salesSQL.join}
  ${salesSQL.where}
  GROUP BY ALL
)

, present AS
(  
  SELECT
    ${usersSQL.splitSelect}
    day_no,
    COUNT(DISTINCT player_id) AS present
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      usersDatasource.sqlMap.dataset,
      usersDatasource.sqlMap.tableId
  )}\` ds
  ${usersSQL.join}
  ${usersSQL.where}
  GROUP BY ALL

)

, cohort_size AS
(
  SELECT
    ${playerDimsSQL.splitGroupBy}
    day_no,
    SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${playerDimsSQL.splitSelect}
      ${playerBiVizDims.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL},
      COUNT(DISTINCT player_id) AS cohort_size,
      ( SELECT all_days FROM \`${options.gcpProjectId}.bi_stg.cohort_days\` cd WHERE cd.first_session_date = player_dim.revenue_sessions.first_session_date ) AS all_days

    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        playerBiVizDims.sqlMap.dataset,
        playerBiVizDims.sqlMap.tableId
    )}\` player_dim
    ${playerDimsSQL.join}
    ${playerDimsSQL.where}
    GROUP BY ALL
  ), UNNEST(all_days) AS day_no 
  GROUP BY ALL
)

, campaign_cost AS
(  
  SELECT
    ${campaignsMastertableSql.splitGroupBy}
    day_no,
    SUM(costs) AS costs
  FROM
  (
    SELECT
      ${campaignsMastertableSql.splitSelect}
      ${
          campaignMastertableDatasource.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE
              ?.columnSQL
      },
      SUM(cost_${currency}) AS costs,
      ( SELECT all_days FROM \`${options.gcpProjectId}.bi_stg.cohort_days\` cd WHERE cd.first_session_date = ds.first_session_date ) AS all_days
        FROM \`${generateTableFullPath(
            options.gcpProjectId,
            campaignMastertableDatasource.sqlMap.dataset,
            campaignMastertableDatasource.sqlMap.tableId
        )}\` ds
    ${campaignsMastertableSql.where}
    GROUP BY ALL
  ), UNNEST(all_days) AS day_no 
  GROUP BY ALL
)

, by_fod AS
(
SELECT
    ${StringUtilsHelper.prefixIfNotNull(playerDimsSQL.splitGroupBy, 'c.')}
    c.day_no,
    ${revenueSql},
    cohort_size,
    costs,
    ROUND(SUM(SAFE_DIVIDE(${revenueSql}, cohort_size)) OVER (${windowRunning}), 4) AS running_arpu,
    ROUND(SUM(SAFE_DIVIDE(${revenueSql}, costs)) OVER (${windowRunning}), 4) AS running_roas,
    ROUND(SUM(SAFE_DIVIDE(campaigns.costs, b.transactions)) OVER (${windowRunning}), 4) AS cpt,
    ROUND(SAFE_DIVIDE(p.present, cohort_size), 4) AS retention
  FROM cohort_size c
  LEFT JOIN revenue b
    ON c.day_no = b.day_no
    ${
        playerDimsSQL.splitGroup === ''
            ? ''
            : `AND c.${playerDimsSQL.splitGroup} = b.${playerDimsSQL.splitGroup}`
    }
  LEFT JOIN campaign_cost campaigns
    ON campaigns.day_no = b.day_no
    ${
        playerDimsSQL.splitGroup === ''
            ? ''
            : `AND c.${playerDimsSQL.splitGroup} = campaigns.${playerDimsSQL.splitGroup}`
    }
  LEFT JOIN present p
    ON campaigns.day_no = p.day_no
    ${
        playerDimsSQL.splitGroup === ''
            ? ''
            : `AND c.${playerDimsSQL.splitGroup} = p.${playerDimsSQL.splitGroup}`
    }
)
  
SELECT *
FROM by_fod
ORDER BY ${playerDimsSQL.splitGroupBy}day_no
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}

export class CampaignsMastertableDocumentation {
    static campaignsTable = /* HTML */ ` <h2>Sources of metrics</h2>
        <pre>
- costs: AppsFlyer
- income: BIEvents
- reattribution_income: BIEvents + reattribution from BIEvents level campaign info 
- total_roas: revenue: BIEvents, cost: AppsFlyer 
- ad_revenue_share: revenue: BIEvents 
- impressions: AppsFlyer 
- installs: AppsFlyer
- transactions: BIEvents 
- payers: BIEvents 
- payers_d7: BIEvents
- cpi: AppsFlyer 
- cpt: cost: - AppsFlyer; transactions: BIEvents
- cmp: AppsFlyer 
- cppu: cost: AppsFlyer; payers: BIEvents 
- cppu_d7: cost: AppsFlyer; payers_d7: BIEvents 
- ipm: AppsFlyer 
- roas_dN: revenue: BIEvents, cost: AppsFlyer 
- arpu_dN: revenue: BIEvents, present_eq_d0: BIEvents 
UWAGA! arpu_dN nie uwzględnia installs z AppsFlyer tylko liczbę użytkowników w d0 z BIEvents (aby zachować spójność z arpu na pozostałych dashboardach) 
- cpt_dN: cost: AppsFlyer; transactions: BIEvents
        </pre
        >`;
}
